// src/Utils/fotosService.js
// Fuente única de verdad de las fotos de una sesión.
//
// La tabla `fotos` es el índice (descripción, orden, destacada) y el bucket
// `fotos` guarda los archivos. Tanto el admin como la vista del cliente leen
// SIEMPRE de acá, así que lo que edites en el admin se ve en el cliente.
import { supabase } from "../Supabase/SupabaseClient";

export const BUCKET = "fotos";

/**
 * Extrae la key del bucket desde una public URL de Supabase Storage.
 * Acepta las dos formas que aparecen en la base:
 *   .../object/public/<bucket>/<key>
 *   .../object/sign/<bucket>/<key>
 */
export const storageKeyFromUrl = (url) => {
  if (!url || typeof url !== "string") return null;

  // El path se guarda en la fila cuando está disponible: es más confiable
  // que parsear la URL.
  try {
    const u = new URL(url);
    const path = u.pathname || "";
    for (const marker of ["/object/public/", "/object/sign/"]) {
      const idx = path.indexOf(marker);
      if (idx >= 0) {
        const rest = path.slice(idx + marker.length); // "<bucket>/<key...>"
        const slash = rest.indexOf("/");
        if (slash >= 0) return decodeURIComponent(rest.slice(slash + 1));
        return null;
      }
    }
  } catch {
    /* URL inválida: caemos al fallback */
  }

  const idx2 = url.indexOf("/fotos/");
  if (idx2 >= 0) return decodeURIComponent(url.slice(idx2 + 1));

  return null;
};

/**
 * Lista las fotos de una sesión desde la tabla `fotos`, ordenadas.
 * @returns {Promise<Array>}
 */
export const listarFotos = async (sesionId) => {
  if (!sesionId) return [];

  // OJO: no seleccionar `path` de forma explícita. Si esa columna no existe en
  // la tabla, PostgREST tira error y la consulta entera falla. La key del
  // storage se deduce de la URL con `storageKeyFromUrl`.
  const { data, error } = await supabase
    .from("fotos")
    .select("id, sesion_id, url_foto, descripcion, orden, destacada")
    .eq("sesion_id", sesionId)
    .order("orden", { ascending: true });

  if (error) throw error;

  return (data || []).map((f, i) => ({
    id: f.id,
    sesion_id: f.sesion_id,
    url_foto: f.url_foto,
    descripcion: f.descripcion ?? "",
    orden: typeof f.orden === "number" ? f.orden : i,
    destacada: Boolean(f.destacada),
  }));
};

/**
 * Borra una foto: primero la fila y después el archivo del storage.
 * Si el storage falla no se corta, porque la fila ya no existe y el archivo
 * huérfano no molesta al cliente.
 *
 * @returns {Promise<{ok: boolean, error?: string, archivoOrfano?: boolean}>}
 */
export const borrarFoto = async (foto) => {
  if (!foto?.id) return { ok: false, error: "La foto no tiene ID." };

  const { error: errorDb } = await supabase
    .from("fotos")
    .delete()
    .eq("id", foto.id)
    .select("id");

  if (errorDb) {
    return {
      ok: false,
      error:
        errorDb.code === "42501"
          ? "No tenés permisos para borrar fotos. Revisá las políticas RLS de la tabla 'fotos'."
          : errorDb.message,
    };
  }

  const key = foto.path || storageKeyFromUrl(foto.url_foto);

  if (!key) {
    // Sin key no podemos tocar el storage, pero la fila ya se borró.
    return { ok: true, archivoOrfano: true };
  }

  const { error: errorStorage } = await supabase.storage.from(BUCKET).remove([key]);

  if (errorStorage) {
    console.warn("Foto borrada de la base pero no del storage:", errorStorage);
    return { ok: true, archivoOrfano: true };
  }

  return { ok: true };
};

/**
 * Reordena una foto moviéndola `delta` posiciones e intercambiando el `orden`
 * con su vecina. Guardar `orden ± 1` a secas genera duplicados y el orden
 * queda indefinido, así que acá se hace un swap real.
 *
 * @param {Array} fotos - lista actual ordenada
 * @param {string|number} fotoId
 * @param {number} delta - -1 sube, +1 baja
 * @returns {Promise<{ok: boolean, error?: string, orden?: number}>}
 */
export const reordenarFoto = async (fotos, fotoId, delta) => {
  const lista = [...(fotos || [])].sort((a, b) => a.orden - b.orden);
  const i = lista.findIndex((f) => String(f.id) === String(fotoId));
  const j = i + delta;

  if (i === -1 || j < 0 || j >= lista.length) {
    return { ok: false, error: "No se puede mover más en esa dirección." };
  }

  const actual = lista[i];
  const vecina = lista[j];

  const { error } = await supabase
    .from("fotos")
    .update({ orden: vecina.orden })
    .eq("id", actual.id);

  if (error) return { ok: false, error: error.message };

  const { error: error2 } = await supabase
    .from("fotos")
    .update({ orden: actual.orden })
    .eq("id", vecina.id);

  if (error2) {
    // Dejamos el estado consistente revirtiendo el primero
    await supabase.from("fotos").update({ orden: actual.orden }).eq("id", actual.id);
    return { ok: false, error: error2.message };
  }

  return { ok: true, orden: vecina.orden };
};

/**
 * Marca una foto como destacada. Si `exclusiva` es true, desmarca las demás
 * de la misma sesión para que solo haya una portada.
 */
export const marcarDestacada = async (foto, { exclusiva = true } = {}) => {
  if (!foto?.id) return { ok: false, error: "La foto no tiene ID." };

  const { error } = await supabase
    .from("fotos")
    .update({ destacada: !foto.destacada })
    .eq("id", foto.id);

  if (error) return { ok: false, error: error.message };

  if (exclusiva && !foto.destacada && foto.sesion_id) {
    await supabase
      .from("fotos")
      .update({ destacada: false })
      .eq("sesion_id", foto.sesion_id)
      .neq("id", foto.id);
  }

  return { ok: true, destacada: !foto.destacada };
};

/** Guarda la descripción de una foto. */
export const guardarDescripcion = async (fotoId, descripcion) => {
  const { error } = await supabase
    .from("fotos")
    .update({ descripcion })
    .eq("id", fotoId);

  if (error) return { ok: false, error: error.message };
  return { ok: true };
};

/**
 * Sube archivos a una sesión. Inserta una fila en `fotos` por cada archivo y
 * limpia del storage lo que haya fallado en la base.
 *
 * @param {string} sesionId
 * @param {File[]} archivos
 * @param {(hechos: number, total: number) => void} onProgress
 */
export const subirFotos = async (sesionId, archivos, onProgress) => {
  const lista = (archivos || []).filter((f) => f && f.size > 0);
  if (!sesionId) throw new Error("Seleccioná una sesión antes de subir fotos.");
  if (lista.length === 0) throw new Error("No hay fotos válidas para subir.");

  const { data: ultima, error: errUltima } = await supabase
    .from("fotos")
    .select("orden")
    .eq("sesion_id", sesionId)
    .order("orden", { ascending: false })
    .limit(1);

  if (errUltima) console.warn("No se pudo leer el último orden:", errUltima);

  let siguiente = 0;
  if (Array.isArray(ultima) && ultima.length > 0 && typeof ultima[0].orden === "number") {
    siguiente = ultima[0].orden + 1;
  }

  const subidas = [];
  const errores = [];

  for (let i = 0; i < lista.length; i++) {
    const file = lista[i];
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const nombre = `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
    const filePath = `sesiones/${sesionId}/${nombre}`;

    const { error: errorUpload } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, { contentType: file.type, upsert: false });

    if (errorUpload) {
      errores.push(`${file.name}: ${errorUpload.message}`);
      onProgress?.(i + 1, lista.length);
      continue;
    }

    const { data: urlData, error: errorUrl } = supabase.storage
      .from(BUCKET)
      .getPublicUrl(filePath);

    if (errorUrl) {
      await supabase.storage.from(BUCKET).remove([filePath]);
      errores.push(`${file.name}: ${errorUrl.message}`);
      onProgress?.(i + 1, lista.length);
      continue;
    }

    const { error: errorDb } = await supabase
      .from("fotos")
      .insert([
        {
          sesion_id: sesionId,
          url_foto: urlData.publicUrl,
          descripcion: "",
          orden: siguiente,
          destacada: false,
        },
      ]);

    if (errorDb) {
      // No dejamos el archivo huérfano en el storage
      await supabase.storage.from(BUCKET).remove([filePath]);
      errores.push(`${file.name}: ${errorDb.message}`);
    } else {
      siguiente += 1;
      subidas.push(file.name);
    }

    onProgress?.(i + 1, lista.length);
  }

  return { subidas, errores };
};
